// Authentication Service
// This service handles all authentication logic, API calls, and business rules
// Separated from UI components for better maintainability and testing

import { apiService, type ApiResponse } from '@/services/apiService';
import { clearRememberMe } from '@/features/auth/utils/rememberMeStorage';

export interface UserCredentials {
    username?: string;
    password: string;
    email: string;
    emailCode: string;
    phone?: string;
    phoneCode?: string;
}

export interface AuthResponse {
    success: boolean;
    message: string;
    data?: any;
    error?: string;
}

export interface EmailCodeResponse {
    success: boolean;
    message: string;
    cooldownSeconds?: number;
    credentialsValid?: boolean;
    error?: string;
    email?: string;
    maskedEmail?: string;
}

export interface CredentialsValidationResponse {
    success: boolean;
    message: string;
    cooldownSeconds?: number;
    credentialsValid?: boolean;
    error?: string;
    // New fields from updated API
    two_factor_enabled?: boolean;
    token?: string;
    refreshToken?: string;
    user?: any;
    expiresAt?: string;
    /** Full email for sendLoginCode (when known from identifier). */
    email?: string;
    /** Display hint e.g. j***@example.com */
    maskedEmail?: string;
    /** Backend already emailed the OTP on credential challenge. */
    codeSent?: boolean;
}

export interface ValidationResult {
    isValid: boolean;
    message: string;
}

class AuthService {
    private readonly RESEND_COOLDOWN_SECONDS = 60; // 60 seconds cooldown
    private emailCooldowns: Map<string, number> = new Map(); // Email-specific cooldowns
    private phoneCooldowns: Map<string, number> = new Map(); // Phone-specific cooldowns
    private cleanupInterval: NodeJS.Timeout | null = null;

    constructor() {
        // Start automatic cleanup of expired cooldowns every 30 seconds
        this.startCleanupInterval();
    }

    /**
     * Starts the automatic cleanup interval for expired cooldowns
     */
    private startCleanupInterval(): void {
        // Clean up expired entries every 30 seconds
        this.cleanupInterval = setInterval(() => {
            this.cleanupExpiredCooldowns();
        }, 30000);
    }

    /**
     * Cleans up expired cooldown entries to prevent memory leaks
     */
    private cleanupExpiredCooldowns(): void {
        const now = Date.now();
        const expiredEmails: string[] = [];
        const expiredPhones: string[] = [];

        // Find expired email entries
        for (const [email, cooldownEndTime] of this.emailCooldowns.entries()) {
            if (now >= cooldownEndTime) {
                expiredEmails.push(email);
            }
        }

        // Find expired phone entries
        for (const [phone, cooldownEndTime] of this.phoneCooldowns.entries()) {
            if (now >= cooldownEndTime) {
                expiredPhones.push(phone);
            }
        }

        // Remove expired entries
        expiredEmails.forEach(email => {
            this.emailCooldowns.delete(email);
        });

        expiredPhones.forEach(phone => {
            this.phoneCooldowns.delete(phone);
        });

        // Log cleanup activity (can be removed in production)
        if (expiredEmails.length > 0 || expiredPhones.length > 0) {
            console.log(`Cleaned up ${expiredEmails.length} expired email cooldown entries and ${expiredPhones.length} expired phone cooldown entries`);
        }
    }

    /**
     * Validates email format using regex
     */
    validateEmail(email: string): ValidationResult {
        if (!email || email.trim() === '') {
            return {
                isValid: false,
                message: 'Email is required'
            };
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            return {
                isValid: false,
                message: 'Please enter a valid email address'
            };
        }

        return {
            isValid: true,
            message: 'Valid email'
        };
    }


    /**
     * Validates password strength
     */
    validatePassword(password: string): ValidationResult {
        if (!password || password === '') {
            return {
                isValid: false,
                message: 'Password is required'
            };
        }

        if (password.length < 6) {
            return {
                isValid: false,
                message: 'Password must be at least 6 characters'
            };
        }

        if (password.length > 128) {
            return {
                isValid: false,
                message: 'Password must be less than 128 characters'
            };
        }

        return {
            isValid: true,
            message: 'Valid password'
        };
    }

    /**
     * Validates email authorization code
     */
    validateEmailCode(code: string): ValidationResult {
        if (!code || code.trim() === '') {
            return {
                isValid: false,
                message: 'Authorization code is required'
            };
        }

        if (code.trim().length !== 6) {
            return {
                isValid: false,
                message: 'Authorization code must be 6 digits'
            };
        }

        const codeRegex = /^\d{6}$/;
        if (!codeRegex.test(code.trim())) {
            return {
                isValid: false,
                message: 'Authorization code must contain only numbers'
            };
        }

        return {
            isValid: true,
            message: 'Valid authorization code'
        };
    }

    /**
     * Validates phone number format (international format with country code)
     * Supports formats like: +16471234567, +442071234567
     */
    validatePhone(phone: string): ValidationResult {
        if (!phone || phone.trim() === '') {
            return {
                isValid: false,
                message: 'Phone number is required'
            };
        }

        const cleanedPhone = phone.trim();

        // Must start with + for international format
        if (!cleanedPhone.startsWith('+')) {
            return {
                isValid: false,
                message: 'Phone number must start with + followed by country code (e.g., +1, +84, +44)'
            };
        }

        // Remove + and check if remaining is all digits
        const digitsOnly = cleanedPhone.substring(1).replace(/\D/g, '');

        if (digitsOnly.length === 0) {
            return {
                isValid: false,
                message: 'Please enter a valid phone number with country code'
            };
        }

        // International phone number format:
        // - Country code: 1-3 digits (e.g., +1 for US/Canada, +84 for Vietnam, +44 for UK)
        // - Subscriber number: 7-12 digits
        // - Total length after +: 8-15 digits
        if (digitsOnly.length < 8 || digitsOnly.length > 15) {
            return {
                isValid: false,
                message: 'Phone number must be 8-15 digits after country code (e.g., +16471234567)'
            };
        }

        // Validate country code (first 1-3 digits should be valid)
        // Country codes range from 1-999, but most common are 1-3 digits
        const countryCode = digitsOnly.substring(0, Math.min(3, digitsOnly.length - 7));
        const subscriberNumber = digitsOnly.substring(countryCode.length);

        // Country code should be 1-3 digits
        if (countryCode.length < 1 || countryCode.length > 3) {
            return {
                isValid: false,
                message: 'Invalid country code. Please use format +[country code][phone number]'
            };
        }

        // Subscriber number should be at least 7 digits
        if (subscriberNumber.length < 7) {
            return {
                isValid: false,
                message: 'Phone number is too short. Please include full number with country code'
            };
        }

        return {
            isValid: true,
            message: 'Valid phone number'
        };
    }

    /**
     * Validates OTP code (same validation for both email and phone)
     */
    validateOTPCode(code: string): ValidationResult {
        if (!code || code.trim() === '') {
            return {
                isValid: false,
                message: 'OTP code is required'
            };
        }

        if (code.trim().length !== 6) {
            return {
                isValid: false,
                message: 'OTP code must be 6 digits'
            };
        }

        const codeRegex = /^\d{6}$/;
        if (!codeRegex.test(code.trim())) {
            return {
                isValid: false,
                message: 'OTP code must contain only numbers'
            };
        }

        return {
            isValid: true,
            message: 'Valid OTP code'
        };
    }

    /**
     * Validates all form fields at once
     * @param requireEmailCode - Whether email code is required (default: false for 2FA optional flow)
     */
    validateAllFields(credentials: UserCredentials, requireEmailCode: boolean = false): ValidationResult {
        const emailValidation = this.validateEmail(credentials.email);
        if (!emailValidation.isValid) return emailValidation;

        const passwordValidation = this.validatePassword(credentials.password);
        if (!passwordValidation.isValid) return passwordValidation;

        // Only validate email code if required (when 2FA is enabled)
        if (requireEmailCode) {
            const codeValidation = this.validateEmailCode(credentials.emailCode);
            if (!codeValidation.isValid) return codeValidation;
        }

        return {
            isValid: true,
            message: 'All fields are valid'
        };
    }

    /**
     * Validates only email and password (for initial credential validation)
     */
    validateCredentials(identifier: string, password: string): ValidationResult {
        if (!identifier || !identifier.trim()) {
            return {
                isValid: false,
                message: 'Username or email is required'
            };
        }

        const passwordValidation = this.validatePassword(password);
        if (!passwordValidation.isValid) return passwordValidation;

        return {
            isValid: true,
            message: 'Credentials are valid'
        };
    }

    /**
     * Checks if resend code button is in cooldown for a specific email
     */
    isResendInCooldown(email: string): boolean {
        const normalizedEmail = email.trim().toLowerCase();
        const cooldownEndTime = this.emailCooldowns.get(normalizedEmail);

        if (!cooldownEndTime) return false;

        // Auto-cleanup expired entry
        if (Date.now() >= cooldownEndTime) {
            this.emailCooldowns.delete(normalizedEmail);
            return false;
        }

        return true;
    }

    /**
     * Gets remaining cooldown time in seconds for a specific email
     */
    getRemainingCooldownSeconds(email: string): number {
        const normalizedEmail = email.trim().toLowerCase();
        const cooldownEndTime = this.emailCooldowns.get(normalizedEmail);

        if (!cooldownEndTime) return 0;

        const now = Date.now();
        if (now >= cooldownEndTime) {
            // Auto-cleanup expired entry
            this.emailCooldowns.delete(normalizedEmail);
            return 0;
        }

        return Math.ceil((cooldownEndTime - now) / 1000);
    }

    /**
     * Starts the resend cooldown timer for a specific email
     */
    startResendCooldown(email: string): void {
        const normalizedEmail = email.trim().toLowerCase();
        const cooldownEndTime = Date.now() + (this.RESEND_COOLDOWN_SECONDS * 1000);
        this.emailCooldowns.set(normalizedEmail, cooldownEndTime);
    }

    /**
     * Checks if resend code button is in cooldown for a specific phone
     */
    isPhoneResendInCooldown(phone: string): boolean {
        const normalizedPhone = phone.trim();
        const cooldownEndTime = this.phoneCooldowns.get(normalizedPhone);

        if (!cooldownEndTime) return false;

        // Auto-cleanup expired entry
        if (Date.now() >= cooldownEndTime) {
            this.phoneCooldowns.delete(normalizedPhone);
            return false;
        }

        return true;
    }

    /**
     * Gets remaining cooldown time in seconds for a specific phone
     */
    getPhoneRemainingCooldownSeconds(phone: string): number {
        const normalizedPhone = phone.trim();
        const cooldownEndTime = this.phoneCooldowns.get(normalizedPhone);

        if (!cooldownEndTime) return 0;

        const now = Date.now();
        if (now >= cooldownEndTime) {
            // Auto-cleanup expired entry
            this.phoneCooldowns.delete(normalizedPhone);
            return 0;
        }

        return Math.ceil((cooldownEndTime - now) / 1000);
    }

    /**
     * Starts the resend cooldown timer for a specific phone
     */
    startPhoneResendCooldown(phone: string): void {
        const normalizedPhone = phone.trim();
        const cooldownEndTime = Date.now() + (this.RESEND_COOLDOWN_SECONDS * 1000);
        this.phoneCooldowns.set(normalizedPhone, cooldownEndTime);
    }

    /** Mask email for OTP hints: j***@example.com */
    maskEmail(email: string): string {
        const trimmed = email.trim();
        const at = trimmed.indexOf('@');
        if (at <= 0) return trimmed;
        const local = trimmed.slice(0, at);
        const domain = trimmed.slice(at + 1);
        const visible = local.slice(0, 1) || '*';
        return `${visible}***@${domain}`;
    }

    /**
     * Validates user credentials via API (email) or login probe (username).
     * Returns token if 2FA is disabled, or validation info if 2FA is enabled.
     */
    async validateCredentialsWithAPI(identifier: string, password: string): Promise<CredentialsValidationResponse> {
        try {
            const trimmed = identifier.trim();
            const passwordValidation = this.validatePassword(password);
            if (!passwordValidation.isValid) {
                return {
                    success: false,
                    message: passwordValidation.message,
                    error: 'VALIDATION_ERROR'
                };
            }

            const emailValidation = this.validateEmail(trimmed);
            // Username / non-email: probe /auth/login (supports identifier) for 2FA detection.
            if (!emailValidation.isValid) {
                const loginResult = await this.authenticateUser({
                    username: trimmed,
                    email: '',
                    password,
                    emailCode: '',
                    phone: '',
                    phoneCode: '',
                });
                if (loginResult.success) {
                    return {
                        success: true,
                        message: loginResult.message || 'Credentials validated successfully',
                        credentialsValid: true,
                        two_factor_enabled: false,
                        token: loginResult.data?.token,
                        refreshToken: loginResult.data?.refreshToken,
                        user: loginResult.data?.user,
                        expiresAt: loginResult.data?.expiresAt,
                    };
                }
                if (loginResult.error === '2FA_REQUIRED') {
                    const email =
                        (typeof loginResult.data?.email === 'string' && loginResult.data.email) ||
                        undefined;
                    const masked =
                        (typeof loginResult.data?.maskedEmail === 'string' && loginResult.data.maskedEmail) ||
                        (email ? this.maskEmail(email) : undefined);
                    const codeSent = loginResult.data?.codeSent === true;
                    const cooldownSeconds =
                        typeof loginResult.data?.cooldownSeconds === 'number'
                            ? loginResult.data.cooldownSeconds
                            : undefined;
                    return {
                        success: true,
                        message: loginResult.message || '2FA is enabled. Email code is required',
                        credentialsValid: true,
                        two_factor_enabled: true,
                        email,
                        maskedEmail: masked,
                        codeSent,
                        cooldownSeconds,
                    };
                }
                return {
                    success: false,
                    message: loginResult.message || 'Invalid credentials',
                    error: loginResult.error || 'API_ERROR',
                    cooldownSeconds: loginResult.data?.cooldownSeconds,
                };
            }

            // Make API call to backend to validate credentials
            // API now returns token if 2FA is disabled, or just validation info if 2FA is enabled
            const response = await apiService.post('/auth/validate-credentials', {
                email: trimmed,
                password: password
            }, false); // Don't include auth for validation

            if (response.success) {
                const responseData = (response.data || {}) as {
                    two_factor_enabled?: boolean;
                    token?: string;
                    refreshToken?: string;
                    user?: unknown;
                    expiresAt?: string;
                    credentialsValid?: boolean;
                    email?: string;
                    maskedEmail?: string;
                    codeSent?: boolean;
                    cooldownSeconds?: number;
                };
                const twoFactorEnabled = responseData.two_factor_enabled || false;
                const token = responseData.token;
                const refreshToken = responseData.refreshToken;
                const user = responseData.user;
                const expiresAt = responseData.expiresAt;

                // If token is provided (2FA disabled), store it immediately
                if (token) {
                    apiService.setTokens(token, refreshToken || '');
                }

                return {
                    success: true,
                    message: response.message || 'Credentials validated successfully',
                    credentialsValid: responseData.credentialsValid !== undefined ? responseData.credentialsValid : true,
                    two_factor_enabled: twoFactorEnabled,
                    token: token,
                    refreshToken: refreshToken,
                    user: user,
                    expiresAt: expiresAt,
                    email:
                        (typeof responseData.email === 'string' && responseData.email.trim()) ||
                        trimmed,
                    maskedEmail:
                        (typeof responseData.maskedEmail === 'string' && responseData.maskedEmail) ||
                        this.maskEmail(
                            (typeof responseData.email === 'string' && responseData.email.trim()) ||
                                trimmed,
                        ),
                    codeSent: responseData.codeSent === true,
                    cooldownSeconds:
                        typeof responseData.cooldownSeconds === 'number'
                            ? responseData.cooldownSeconds
                            : undefined,
                };
            } else {
                return {
                    success: false,
                    message: response.message || 'Invalid credentials',
                    error: 'API_ERROR'
                };
            }

        } catch (error: any) {
            console.error('Error validating credentials:', {
                message: error?.message || 'Unknown error',
                statusCode: error?.statusCode || 'No status code',
                stack: error?.stack || 'No stack trace'
            });

            // Handle specific error cases
            if (error?.statusCode === 401) {
                return {
                    success: false,
                    message: error?.message || 'Invalid email or password',
                    error: 'INVALID_CREDENTIALS'
                };
            }

            if (error?.statusCode === 403) {
                return {
                    success: false,
                    message: error?.message || 'Account is locked or banned',
                    error: 'FORBIDDEN'
                };
            }

            if (error?.statusCode === 429) {
                return {
                    success: false,
                    message: error?.message || 'Too many requests. Please wait before trying again.',
                    cooldownSeconds: error?.data?.cooldownSeconds || this.RESEND_COOLDOWN_SECONDS,
                    error: 'RATE_LIMITED'
                };
            }

            return {
                success: false,
                message: error?.message || 'Failed to validate credentials. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Sends login code after credentials validation.
     * Accepts registered email or username (backend resolves the account).
     */
    async sendLoginCode(identifier: string): Promise<EmailCodeResponse> {
        try {
            const trimmed = identifier.trim();
            if (!trimmed) {
                return {
                    success: false,
                    message: 'Email or username is required',
                    error: 'VALIDATION_ERROR'
                };
            }

            const cooldownKey = trimmed.toLowerCase();
            // Check cooldown for this specific identifier
            if (this.isResendInCooldown(cooldownKey)) {
                return {
                    success: false,
                    message: `Please wait ${this.getRemainingCooldownSeconds(cooldownKey)} seconds before requesting another code`,
                    cooldownSeconds: this.getRemainingCooldownSeconds(cooldownKey),
                    error: 'COOLDOWN_ACTIVE'
                };
            }

            const emailValidation = this.validateEmail(trimmed);
            const body = emailValidation.isValid
                ? { email: trimmed }
                : { identifier: trimmed, username: trimmed };

            // Make API call to backend to send login code
            const response = await apiService.post('/auth/send-login-code', body, false); // Don't include auth for sending code

            if (response.success) {
                const responseData = (response.data || {}) as {
                    cooldownSeconds?: number;
                    email?: string;
                    maskedEmail?: string;
                };
                // Start cooldown for this specific identifier (and email if returned)
                this.startResendCooldown(cooldownKey);
                if (responseData.email) {
                    this.startResendCooldown(responseData.email);
                }

                return {
                    success: true,
                    message: response.message,
                    cooldownSeconds: responseData.cooldownSeconds || this.RESEND_COOLDOWN_SECONDS,
                    email: responseData.email,
                    maskedEmail: responseData.maskedEmail,
                };
            } else {
                return {
                    success: false,
                    message: response.message || 'Failed to send login code',
                    error: 'API_ERROR'
                };
            }

        } catch (error: any) {
            console.error('Error sending login code:', {
                message: error?.message || 'Unknown error',
                statusCode: error?.statusCode || 'No status code',
                stack: error?.stack || 'No stack trace'
            });

            // Handle specific error cases
            if (error?.statusCode === 401) {
                return {
                    success: false,
                    message: error?.message || 'Invalid email or account not verified',
                    error: 'INVALID_EMAIL'
                };
            }


            if (error?.statusCode === 429) {
                return {
                    success: false,
                    message: error?.message || 'Too many requests. Please wait before trying again.',
                    cooldownSeconds: error?.data?.cooldownSeconds || this.RESEND_COOLDOWN_SECONDS,
                    error: 'RATE_LIMITED'
                };
            }

            return {
                success: false,
                message: error?.message || 'Failed to send login code. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Sends email authorization code (legacy method)
     */
    async sendEmailCode(email: string): Promise<EmailCodeResponse> {
        try {
            // Validate email first
            const emailValidation = this.validateEmail(email);
            if (!emailValidation.isValid) {
                return {
                    success: false,
                    message: emailValidation.message,
                    error: 'INVALID_EMAIL'
                };
            }

            // Check cooldown for this specific email
            if (this.isResendInCooldown(email)) {
                return {
                    success: false,
                    message: `Please wait ${this.getRemainingCooldownSeconds(email)} seconds before requesting another code for this email`,
                    cooldownSeconds: this.getRemainingCooldownSeconds(email),
                    error: 'COOLDOWN_ACTIVE'
                };
            }

            // Make API call to backend
            const response = await apiService.post('/auth/send-code', {
                email: email.trim()
            }, false); // Don't include auth for this endpoint

            // Start cooldown for this specific email
            this.startResendCooldown(email);

            return {
                success: response.success,
                message: response.message,
                cooldownSeconds: (response.data as any)?.cooldownSeconds || this.RESEND_COOLDOWN_SECONDS
            };

        } catch (error: any) {
            console.error('Error sending email code:', error);

            // Handle specific error cases
            if (error.statusCode === 429) {
                return {
                    success: false,
                    message: error.message || 'Too many requests. Please wait before trying again.',
                    cooldownSeconds: error.data?.cooldownSeconds || this.RESEND_COOLDOWN_SECONDS,
                    error: 'RATE_LIMITED'
                };
            }

            return {
                success: false,
                message: error.message || 'Failed to send authorization code. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Sends OTP code to email for email login
     */
    async sendEmailOTP(email: string): Promise<EmailCodeResponse> {
        try {
            // Validate email first
            const emailValidation = this.validateEmail(email);
            if (!emailValidation.isValid) {
                return {
                    success: false,
                    message: emailValidation.message,
                    error: 'INVALID_EMAIL'
                };
            }

            // Check cooldown for this specific email
            if (this.isResendInCooldown(email)) {
                return {
                    success: false,
                    message: `Please wait ${this.getRemainingCooldownSeconds(email)} seconds before requesting another code for this email`,
                    cooldownSeconds: this.getRemainingCooldownSeconds(email),
                    error: 'COOLDOWN_ACTIVE'
                };
            }

            // Make API call to backend
            const response = await apiService.post('/auth/email/send-otp', {
                email: email.trim()
            }, false); // Don't include auth for this endpoint

            if (response.success) {
                // Start cooldown for this specific email
                this.startResendCooldown(email);

                return {
                    success: true,
                    message: response.message,
                    cooldownSeconds: (response.data as any)?.cooldownSeconds || this.RESEND_COOLDOWN_SECONDS
                };
            } else {
                return {
                    success: false,
                    message: response.message || 'Failed to send OTP code',
                    error: 'API_ERROR'
                };
            }

        } catch (error: any) {
            console.error('Error sending email OTP:', error);

            // Handle specific error cases
            if (error.statusCode === 429) {
                return {
                    success: false,
                    message: error.message || 'Too many requests. Please wait before trying again.',
                    cooldownSeconds: error.data?.cooldownSeconds || this.RESEND_COOLDOWN_SECONDS,
                    error: 'RATE_LIMITED'
                };
            }

            return {
                success: false,
                message: error.message || 'Failed to send OTP code. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Sends OTP code to phone number for login
     */
    async sendPhoneLoginCode(phone: string): Promise<EmailCodeResponse> {
        try {
            // Validate phone first
            const phoneValidation = this.validatePhone(phone);
            if (!phoneValidation.isValid) {
                return {
                    success: false,
                    message: phoneValidation.message,
                    error: 'VALIDATION_ERROR'
                };
            }

            // Check cooldown for this specific phone
            if (this.isPhoneResendInCooldown(phone)) {
                return {
                    success: false,
                    message: `Please wait ${this.getPhoneRemainingCooldownSeconds(phone)} seconds before requesting another code for this phone`,
                    cooldownSeconds: this.getPhoneRemainingCooldownSeconds(phone),
                    error: 'COOLDOWN_ACTIVE'
                };
            }

            // Normalize phone number: keep + and digits only
            const normalizedPhone = '+' + phone.trim().substring(1).replace(/\D/g, '');

            // Make API call to backend to send OTP
            const response = await apiService.post('/auth/phone/send-otp', {
                phone: normalizedPhone
            }, false); // Don't include auth for sending OTP

            if (response.success) {
                // Start cooldown for this specific phone
                this.startPhoneResendCooldown(phone);

                return {
                    success: true,
                    message: response.message || 'OTP code sent to your phone',
                    cooldownSeconds: (response.data as any)?.cooldownSeconds || this.RESEND_COOLDOWN_SECONDS
                };
            } else {
                return {
                    success: false,
                    message: response.message || 'Failed to send OTP code',
                    error: 'API_ERROR'
                };
            }

        } catch (error: any) {
            console.error('Error sending phone login code:', {
                message: error?.message || 'Unknown error',
                statusCode: error?.statusCode || 'No status code',
                stack: error?.stack || 'No stack trace'
            });

            // Handle specific error cases
            if (error?.statusCode === 400) {
                return {
                    success: false,
                    message: error?.message || 'Invalid phone number',
                    error: 'INVALID_PHONE'
                };
            }

            if (error?.statusCode === 429) {
                return {
                    success: false,
                    message: error?.message || 'Too many requests. Please wait before trying again.',
                    cooldownSeconds: error?.data?.cooldownSeconds || this.RESEND_COOLDOWN_SECONDS,
                    error: 'RATE_LIMITED'
                };
            }

            return {
                success: false,
                message: error?.message || 'Failed to send OTP code. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Authenticates user with phone number and OTP code
     */
    async loginWithPhoneCode(phone: string, otp: string): Promise<AuthResponse> {
        try {
            // Validate phone
            const phoneValidation = this.validatePhone(phone);
            if (!phoneValidation.isValid) {
                return {
                    success: false,
                    message: phoneValidation.message,
                    error: 'VALIDATION_ERROR'
                };
            }

            // Validate OTP code
            const otpValidation = this.validateOTPCode(otp);
            if (!otpValidation.isValid) {
                return {
                    success: false,
                    message: otpValidation.message,
                    error: 'VALIDATION_ERROR'
                };
            }

            // Normalize phone number: keep + and digits only
            const normalizedPhone = '+' + phone.trim().substring(1).replace(/\D/g, '');

            // Make API call to backend
            const response = await apiService.post('/auth/phone/login', {
                phone: normalizedPhone,
                otp: otp.trim()
            }, false); // Don't include auth for login

            // Check if login was successful
            if (response.success) {
                // Extract data - response.data should contain the actual response data
                const responseData = (response.data || {}) as {
                    token?: string;
                    refreshToken?: string;
                    user?: unknown;
                    expiresAt?: string;
                };

                // Extract tokens and user info from response data
                const token = responseData.token;
                const refreshToken = responseData.refreshToken;
                const user = responseData.user;

                if (token) {
                    // Store authentication tokens
                    apiService.setTokens(token, refreshToken || '');

                    // Clear cache on login to ensure fresh data for new user session
                    apiService.clearUserCache();

                    // Pre-fetch all important APIs in background for faster subsequent loads
                    // This runs asynchronously and doesn't block the login flow
                    apiService.prefetchUserData();

                    return {
                        success: true,
                        message: response.message || 'Login successful',
                        data: {
                            user: user,
                            token: token,
                            refreshToken: refreshToken,
                            expiresAt: responseData.expiresAt,
                            authenticatedAt: new Date().toISOString()
                        }
                    };
                } else {
                    // Success but no token - should not happen but handle gracefully
                    console.error('Login successful but no token received:', response);
                    return {
                        success: false,
                        message: response.message || 'Authentication failed: No token received',
                        error: 'AUTHENTICATION_FAILED'
                    };
                }
            } else {
                // Handle case where API returns success: false but doesn't throw error
                // Extract error field from API response if available
                return {
                    success: false,
                    message: response.message || 'Authentication failed',
                    error: response.error || ''
                };
            }

        } catch (error: any) {
            console.error('Error authenticating with phone:', {
                message: error?.message || 'Unknown error',
                statusCode: error?.statusCode || 'No status code',
                stack: error?.stack || 'No stack trace'
            });

            // Handle specific error cases
            if (error?.statusCode === 401) {
                return {
                    success: false,
                    message: error?.message || 'Invalid phone number or OTP code',
                    error: 'INVALID_CREDENTIALS'
                };
            }

            if (error?.statusCode === 400) {
                return {
                    success: false,
                    message: error?.message || 'Invalid OTP code. Please try again.',
                    error: 'INVALID_OTP'
                };
            }

            if (error?.statusCode === 429) {
                return {
                    success: false,
                    message: 'Too many login attempts. Please wait a few minutes before trying again.',
                    error: 'RATE_LIMITED'
                };
            }

            return {
                success: false,
                message: error?.message || 'Authentication failed. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Authenticates user with email and code (passwordless login)
     */
    async loginWithEmailCode(email: string, code: string): Promise<AuthResponse> {
        try {
            // Validate email
            const emailValidation = this.validateEmail(email);
            if (!emailValidation.isValid) {
                return {
                    success: false,
                    message: emailValidation.message,
                    error: 'VALIDATION_ERROR'
                };
            }

            // Validate code
            const codeValidation = this.validateEmailCode(code);
            if (!codeValidation.isValid) {
                return {
                    success: false,
                    message: codeValidation.message,
                    error: 'VALIDATION_ERROR'
                };
            }

            // Make API call to backend
            const response = await apiService.post('/auth/email/login', {
                email: email.trim(),
                otp: code.trim()
            }, false); // Don't include auth for login

            // Check if login was successful
            if (response.success) {
                // Extract data - response.data should contain the actual response data
                const responseData = (response.data || {}) as {
                    token?: string;
                    refreshToken?: string;
                    user?: unknown;
                    expiresAt?: string;
                };

                // Extract tokens and user info from response data
                const token = responseData.token;
                const refreshToken = responseData.refreshToken;
                const user = responseData.user;

                if (token) {
                    // Store authentication tokens
                    apiService.setTokens(token, refreshToken || '');

                    // Clear cache on login to ensure fresh data for new user session
                    apiService.clearUserCache();

                    // Pre-fetch all important APIs in background for faster subsequent loads
                    // This runs asynchronously and doesn't block the login flow
                    apiService.prefetchUserData();

                    return {
                        success: true,
                        message: response.message || 'Login successful',
                        data: {
                            user: user,
                            token: token,
                            refreshToken: refreshToken,
                            expiresAt: responseData.expiresAt,
                            authenticatedAt: new Date().toISOString()
                        }
                    };
                } else {
                    // Success but no token - should not happen but handle gracefully
                    console.error('Login successful but no token received:', response);
                    return {
                        success: false,
                        message: response.message || 'Authentication failed: No token received',
                        error: 'AUTHENTICATION_FAILED'
                    };
                }
            } else {
                // Handle case where API returns success: false but doesn't throw error
                // Extract error field from API response if available
                return {
                    success: false,
                    message: response.message || 'Authentication failed',
                    error: response.error || 'AUTHENTICATION_FAILED'
                };
            }

        } catch (error: any) {
            console.error('Error authenticating with email:', {
                message: error?.message || 'Unknown error',
                statusCode: error?.statusCode || 'No status code',
                stack: error?.stack || 'No stack trace'
            });

            // Handle specific error cases
            if (error?.statusCode === 401) {
                return {
                    success: false,
                    message: error?.message || 'Invalid email or code',
                    error: 'INVALID_CREDENTIALS'
                };
            }

            if (error?.statusCode === 400) {
                return {
                    success: false,
                    message: error?.message || 'Invalid code. Please try again.',
                    error: 'INVALID_CODE'
                };
            }

            if (error?.statusCode === 429) {
                return {
                    success: false,
                    message: 'Too many login attempts. Please wait a few minutes before trying again.',
                    error: 'RATE_LIMITED'
                };
            }

            return {
                success: false,
                message: error?.message || 'Authentication failed. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Authenticates user with credentials
     * @param require2FA - Whether 2FA is required (determined by API response)
     */
    async authenticateUser(credentials: UserCredentials, require2FA: boolean = false): Promise<AuthResponse> {
        try {
            const identifier = (credentials.username || credentials.email || '').trim();
            const credentialsValidation = this.validateCredentials(identifier, credentials.password);
            if (!credentialsValidation.isValid) {
                return {
                    success: false,
                    message: credentialsValidation.message,
                    error: 'VALIDATION_ERROR'
                };
            }

            // If 2FA is required, validate email code
            if (require2FA) {
                const codeValidation = this.validateEmailCode(credentials.emailCode);
                if (!codeValidation.isValid) {
                    return {
                        success: false,
                        message: codeValidation.message,
                        error: 'VALIDATION_ERROR'
                    };
                }
            }

            // Make API call to backend
            // emailCode is optional - backend will check if 2FA is enabled
            const requestBody: any = {
                identifier: identifier.toLowerCase(),
                password: credentials.password
            };

            // Only include emailCode if provided (for 2FA enabled users)
            if (credentials.emailCode && credentials.emailCode.trim()) {
                requestBody.emailCode = credentials.emailCode.trim();
            }

            const response = await apiService.post('/auth/login', requestBody, false); // Don't include auth for login

            // Check if login was successful
            if (response.success) {
                // Extract data - response.data should contain the actual response data
                const responseData = (response.data || {}) as {
                    token?: string;
                    refreshToken?: string;
                    user?: unknown;
                    expiresAt?: string;
                };

                // Extract tokens and user info from response data
                const token = responseData.token;
                const refreshToken = responseData.refreshToken;
                const user = responseData.user;

                if (token) {
                    // Store authentication tokens
                    apiService.setTokens(token, refreshToken || '');

                    // Clear cache on login to ensure fresh data for new user session
                    apiService.clearUserCache();

                    // Pre-fetch all important APIs in background for faster subsequent loads
                    // This runs asynchronously and doesn't block the login flow
                    apiService.prefetchUserData();

                    return {
                        success: true,
                        message: response.message || 'Login successful',
                        data: {
                            user: user,
                            token: token,
                            refreshToken: refreshToken,
                            expiresAt: responseData.expiresAt,
                            authenticatedAt: new Date().toISOString()
                        }
                    };
                } else {
                    // Success but no token - should not happen but handle gracefully
                    console.error('Login successful but no token received:', response);
                    return {
                        success: false,
                        message: response.message || 'Authentication failed: No token received',
                        error: 'AUTHENTICATION_FAILED'
                    };
                }
            } else {
                // Handle case where API returns success: false but doesn't throw error
                // Extract error field from API response if available
                return {
                    success: false,
                    message: response.message || 'Authentication failed',
                    error: response.error || 'AUTHENTICATION_FAILED'
                };
            }

        } catch (error: any) {
            console.error('Error authenticating user:', {
                message: error?.message || 'Unknown error',
                statusCode: error?.statusCode || 'No status code',
                stack: error?.stack || 'No stack trace'
            });

            // Handle specific error cases
            if (error?.statusCode === 401) {
                // Check if error indicates 2FA is required
                const errorMessage = error?.message || '';
                const errData = error?.data && typeof error.data === 'object' ? error.data : {};
                if (errorMessage.toLowerCase().includes('2fa') || errorMessage.toLowerCase().includes('code') || errorMessage.toLowerCase().includes('otp')) {
                    return {
                        success: false,
                        message: errorMessage || '2FA code is required',
                        error: '2FA_REQUIRED',
                        data: { requires2FA: true, ...errData }
                    };
                }
                return {
                    success: false,
                    message: errorMessage || 'Invalid credentials',
                    error: 'INVALID_CREDENTIALS'
                };
            }

            // Handle 400 Bad Request - might indicate 2FA required
            if (error?.statusCode === 400) {
                const errorMessage = error?.message || '';
                const errData = error?.data && typeof error.data === 'object' ? error.data : {};
                if (errorMessage.toLowerCase().includes('2fa') || errorMessage.toLowerCase().includes('code') || errorMessage.toLowerCase().includes('otp')) {
                    return {
                        success: false,
                        message: errorMessage || '2FA code is required',
                        error: '2FA_REQUIRED',
                        data: { requires2FA: true, ...errData }
                    };
                }
            }

            if (error?.statusCode === 403) {
                return {
                    success: false,
                    message: error?.message || 'Account is locked or banned',
                    error: 'FORBIDDEN'
                };
            }

            if (error?.statusCode === 429) {
                return {
                    success: false,
                    message: error?.message || 'Too many login attempts. Please wait a few minutes before trying again.',
                    error: 'RATE_LIMITED',
                    data: { cooldownSeconds: error?.data?.cooldownSeconds }
                };
            }

            return {
                success: false,
                message: error?.message || 'Authentication failed. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Logs out the current user
     */
    async logout(): Promise<AuthResponse> {
        try {
            // Make API call to logout endpoint
            await apiService.post('/auth/logout', {}, true);

            // Clear tokens from storage
            apiService.clearTokens();

            // Clear all user-related cache on logout
            apiService.clearUserCache();

            // Clear "Remember me" preference
            clearRememberMe();

            return {
                success: true,
                message: 'Logged out successfully'
            };

        } catch (error: any) {
            console.error('Error logging out:', error);

            // Even if API call fails, clear local tokens and cache
            apiService.clearTokens();
            apiService.clearUserCache();

            // Clear "Remember me" preference
            clearRememberMe();

            return {
                success: true,
                message: 'Logged out successfully'
            };
        }
    }

    /**
     * Gets the current user profile
     */
    async getCurrentUser(): Promise<AuthResponse> {
        try {
            const response = await apiService.get('/auth/me', true);

            return {
                success: response.success,
                message: response.message,
                data: response.data
            };

        } catch (error: any) {
            console.error('Error getting user profile:', error);

            if (error.statusCode === 401) {
                // Token expired or invalid
                apiService.clearTokens();
                return {
                    success: false,
                    message: 'Session expired. Please log in again.',
                    error: 'SESSION_EXPIRED'
                };
            }

            return {
                success: false,
                message: error.message || 'Failed to get user profile',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Gets the current user's tier (e.g. Platinum, Diamond).
     * GET /api/auth/tier with Bearer token.
     * Supports: data.tier.currentTier.name | data.tier (string) | data.data.tier
     */
    async getTier(): Promise<string | null> {
        try {
            const response = await apiService.get<any>('/auth/tier', true);
            if (!response.success || !response.data) return null;
            const data = response.data;
            const tierObj = data?.tier;
            const name =
                tierObj?.currentTier?.name ??
                (typeof tierObj === 'string' ? tierObj : null) ??
                data?.data?.tier;
            return typeof name === 'string' && name.trim() ? name.trim() : null;
        } catch {
            return null;
        }
    }

    /**
     * Checks if user is currently authenticated
     */
    isAuthenticated(): boolean {
        return apiService.isAuthenticated();
    }

    /**
     * Sends OTP to enable 2FA for the currently authenticated user.
     */
    async sendEnable2FACode(): Promise<EmailCodeResponse> {
        try {
            const response = await apiService.post('/users/enable-2fa/send-code', {}, true);
            if (response.success) {
                return {
                    success: true,
                    message: response.message || 'Verification code sent to your email',
                    cooldownSeconds: (response.data as { cooldownSeconds?: number })?.cooldownSeconds || this.RESEND_COOLDOWN_SECONDS,
                };
            }
            return {
                success: false,
                message: response.message || 'Failed to send verification code',
                error: 'API_ERROR',
            };
        } catch (error: any) {
            if (error?.statusCode === 429) {
                return {
                    success: false,
                    message: error?.message || 'Please wait before requesting another code',
                    cooldownSeconds: error?.data?.cooldownSeconds || this.RESEND_COOLDOWN_SECONDS,
                    error: 'RATE_LIMITED',
                };
            }
            return {
                success: false,
                message: error?.message || 'Failed to send verification code. Please try again.',
                error: 'API_ERROR',
            };
        }
    }

    /**
     * Enables 2FA for the current user
     * @param emailCode - The OTP code received via email
     * @returns Promise with enable result
     */
    async enable2FA(emailCode: string): Promise<AuthResponse> {
        try {
            if (!emailCode || emailCode.trim().length !== 6) {
                return {
                    success: false,
                    message: 'Please enter a valid 6-digit OTP code',
                    error: 'VALIDATION_ERROR'
                };
            }

            const response = await apiService.post('/users/enable-2fa', {
                emailCode: emailCode.trim()
            }, true);

            return {
                success: response.success,
                message: response.message,
                data: response.data
            };

        } catch (error: any) {
            console.error('Error enabling 2FA:', error);

            if (error.statusCode === 401) {
                return {
                    success: false,
                    message: 'Authentication required. Please log in again.',
                    error: 'UNAUTHORIZED'
                };
            }

            if (error.statusCode === 400) {
                return {
                    success: false,
                    message: error.message || 'Invalid OTP code. Please try again.',
                    error: 'INVALID_CODE'
                };
            }

            return {
                success: false,
                message: error.message || 'Failed to enable 2FA. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Disables 2FA for the current user
     * @param password - User's password for verification
     * @returns Promise with disable result
     */
    async disable2FA(password: string): Promise<AuthResponse> {
        try {
            if (!password || password.trim() === '') {
                return {
                    success: false,
                    message: 'Password is required to disable 2FA',
                    error: 'VALIDATION_ERROR'
                };
            }

            const response = await apiService.post('/users/disable-2fa', {
                password: password
            }, true);

            return {
                success: response.success,
                message: response.message,
                data: response.data
            };

        } catch (error: any) {
            console.error('Error disabling 2FA:', error);

            if (error.statusCode === 401) {
                return {
                    success: false,
                    message: 'Authentication required. Please log in again.',
                    error: 'UNAUTHORIZED'
                };
            }

            if (error.statusCode === 400) {
                return {
                    success: false,
                    message: error.message || 'Invalid password. Please try again.',
                    error: 'INVALID_PASSWORD'
                };
            }

            return {
                success: false,
                message: error.message || 'Failed to disable 2FA. Please try again.',
                error: 'NETWORK_ERROR'
            };
        }
    }

    /**
     * Resets the service state (useful for logout or new session)
     */
    reset(): void {
        this.emailCooldowns.clear();
        this.phoneCooldowns.clear();
    }

    /**
     * Destroys the service and cleans up resources
     */
    destroy(): void {
        if (this.cleanupInterval) {
            clearInterval(this.cleanupInterval);
            this.cleanupInterval = null;
        }
        this.emailCooldowns.clear();
        this.phoneCooldowns.clear();
    }

    /**
     * Clears cooldown for a specific email (useful for testing or manual reset)
     */
    clearEmailCooldown(email: string): void {
        const normalizedEmail = email.trim().toLowerCase();
        this.emailCooldowns.delete(normalizedEmail);
    }

    /**
     * Clears cooldown for a specific phone (useful for testing or manual reset)
     */
    clearPhoneCooldown(phone: string): void {
        const normalizedPhone = phone.trim();
        this.phoneCooldowns.delete(normalizedPhone);
    }
}

// Export singleton instance
export const authService = new AuthService();

// Export types for use in components
// Types are already exported above as interfaces
